import type {
	EditorCommand,
	PublicPluginAPI,
	OptionalPlugin,
} from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	remixEditExistingExperience,
	REMIX_EDIT_EXISTING_MARKS,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui';
import type { ConfluenceUpdateDynamicUiOnPageData } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { remixRegenerateExperience } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender/remix-regenerate-experience';

// Format mirrors `getMauiUrl` in @atlassian/malleable-ui-components/src/ui/editor-remix/utils/message.ts.
// Inlined here to avoid a cross-package dependency for one URL formula.
const buildMauiUrl = (appId: string): string =>
	`${window.location.origin}/remix/ui/${encodeURIComponent(appId)}`;

export class DocumentDynamicUiUpdateCommandProcessor {
	constructor(
		private editorApi: PublicPluginAPI<
			[AnalyticsPlugin, AIPlugin, OptionalPlugin<MalleableUiPlugin>]
		>,
	) {}

	// Deferred to finish() so the remix-history transaction is dispatched only
	// after endAIExperience(). Dispatching it while the AI session is still open
	// lets a subsequent click/type discard-and-revert the just-applied update.
	private pendingHistoryStackEntryCommand?: EditorCommand;

	async streamChunk(
		data: ConfluenceUpdateDynamicUiOnPageData,
		isFinished: boolean,
		_traceId?: string,
		_singleInstrumentationId?: string,
	): Promise<void> {
		if (!isFinished) {
			return;
		}

		// Variant-picker updates are inserted FE-side (useUpdateRemix ->
		// useOnComplete); processing the BE push here would double-insert.
		// Rovo-chat updates aren't tracked, so they still flow through.
		if (
			data.previousMauiId &&
			this.editorApi.malleableUi?.actions.isVariantUpdateInFlight(data.previousMauiId) &&
			fg('platform_editor_ai_remix_toolbar')
		) {
			// Mark success so the AI experience overlay ends normally; the
			// update did succeed, the FE just owns the insertion.
			remixRegenerateExperience.success({
				remixType: data.contentType,
				remixSubtype: data.dynamicUiSubtype,
			});
			return;
		}

		// Awaiting matters: it keeps the existing AI experience overlay covering
		// the duplicate roundtrip. `endAIExperience()` is only called from
		// `finish()` after this resolves, via PageUpdateCommandHandler.finishAction.
		try {
			// `duplicateAndReplaceMauiApp` may duplicate the BE-provided MAUI app
			// under the current page (for permissions). When it does, the live
			// node ends up with the duplicated id — NOT `data.id`. Capture that
			// id so the stack entry URL we record matches the live node, which
			// is what the toolbar lookup keys off.
			const replaceResult = await this.editorApi.malleableUi?.actions.duplicateAndReplaceMauiApp({
				previousMauiId: data.previousMauiId,
				newMauiId: data.id,
				localId: data.selectionLocalId,
			});
			remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.NODE_INSERTED);
			remixEditExistingExperience.success();
			remixRegenerateExperience.success({
				remixType: data.contentType,
				remixSubtype: data.dynamicUiSubtype,
			});

			if (data.previousMauiId && fg('platform_editor_ai_remix_toolbar')) {
				// Fall back to data.id if the action didn't run (e.g. malleableUi
				// plugin unavailable) — better to record an entry keyed off the
				// BE id than to skip recording entirely.
				const insertedMauiId = replaceResult?.insertedMauiId ?? data.id;
				// Stash the command; it is dispatched in finish() after the AI
				// experience ends (see field comment).
				this.pendingHistoryStackEntryCommand =
					this.editorApi.malleableUi?.commands.addBackendRemixHistoryStackEntry({
						previousMauiId: data.previousMauiId,
						url: buildMauiUrl(insertedMauiId),
						dynamicUiType: data.contentType,
						dynamicUiSubtype: data.dynamicUiSubtype,
					});
			}
		} catch (error) {
			remixEditExistingExperience.failure({
				metadata: { reason: 'duplicate_replace_failed' },
			});
			remixRegenerateExperience.fail({
				error: error instanceof Error ? error.message : 'duplicate_replace_failed',
			});
			throw error;
		}
	}

	finish(): void {
		this.editorApi.aiExperience?.actions.endAIExperience();
		// Record the remix-history stack entry only after the AI experience has
		// ended, so the transaction does not land in the still-open session where a
		// click/type would discard-and-revert the update.
		if (this.pendingHistoryStackEntryCommand) {
			this.editorApi.core?.actions.execute(this.pendingHistoryStackEntryCommand);
			this.pendingHistoryStackEntryCommand = undefined;
		}
	}
}
